import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getAllowedPriceRestrictedOrderItemQuantity, getLowestItemPrice, getOrderTicketCountForShowtime, getSellableItemForSiteByHopk, isDiscountItemPrice, isMemberRewardItemPrice } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { observer } from 'mobx-react';
import { useShowtimeContext } from '../../../context/showtime/hooks/use-showtime-context';
import { getAllowedMaxQuantityForItem } from '../../../utils/items/get-allowed-max-quantity-for-item';
import { withComponentWrapper } from '../../../utils/with-component-wrapper';
import { SingleItemPickerComponent, SingleItemPickerSkeleton } from '../../single-item-picker';
import { defaultThreeDGlassesPickerProps, threeDGlassesPickerValidator } from './types/props';
import { setInitialQuantity } from './utils/set-initial-quantity';
const b = bemClassBuilder('v-3d-glasses-picker');
export const ThreeDGlassesPicker = observer(props => {
    const { itemHopk, config, displayText, assets } = props;
    const { showtime, showtimeId } = useShowtimeContext();
    if (!showtime)
        return _jsx(SingleItemPickerSkeleton, {});
    if (!showtime.requires3dGlasses)
        return null;
    const sellableItem = getSellableItemForSiteByHopk(showtime.siteId, itemHopk);
    const selectedTicketCount = getOrderTicketCountForShowtime(showtimeId);
    const handleItemModalOpened = () => {
        if (!sellableItem)
            return;
        const lowestPrice = getLowestItemPrice(sellableItem.prices);
        if (isDiscountItemPrice(lowestPrice) || isMemberRewardItemPrice(lowestPrice)) {
            const allowedMaximumForPriceRestrictedItems = getAllowedPriceRestrictedOrderItemQuantity(getAllowedMaxQuantityForItem(config.quantityLimit), sellableItem.itemId, false);
            if (allowedMaximumForPriceRestrictedItems < selectedTicketCount)
                return; // A design decision has been made where if there are not enough discounts available to match the ticket selection count, then we will not set an initial quantity for a single item.
        }
        setInitialQuantity(sellableItem, selectedTicketCount);
    };
    // Omit header property of 3D Glasses Picker config before passing to Single Item Picker config as header does not exist there
    const { header } = config, singleItemPickerConfig = __rest(config, ["header"]);
    // Omit headerLabel property of 3D Glasses Picker display text before passing to Single Item Picker display text as headerLabel does not exist there
    const { headerLabel } = displayText, singleItemPickerDisplayText = __rest(displayText, ["headerLabel"]);
    return (_jsxs("div", Object.assign({ className: b.block() }, { children: [header.show && _jsx("h3", Object.assign({ className: b.element('header') }, { children: headerLabel })), _jsx(SingleItemPickerComponent, { itemHopk: itemHopk, siteId: showtime.siteId, events: { onItemModalOpen: handleItemModalOpened }, displayText: singleItemPickerDisplayText, config: singleItemPickerConfig, assets: assets })] })));
});
ThreeDGlassesPicker.displayName = 'ThreeDGlassesPicker';
/**
 * The **3D Glasses Picker** component renders the single item picker for a 3D glasses item, the initial item quantity is set to the amount of tickets selected.
 */
export const ThreeDGlassesPickerComponent = withComponentWrapper(ThreeDGlassesPicker, defaultThreeDGlassesPickerProps, threeDGlassesPickerValidator);
